import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { AlertTriangle, KeyRound, X } from 'lucide-react';

/**
 * Banner global de alerta de expiração dos tokens KAKO (agência e DM).
 * Aparece em qualquer aba do painel admin quando um token está:
 * - expirado (vermelho)
 * - a <= 7 dias de expirar (laranja)
 * Usa o modo quick dos endpoints (decodifica exp do JWT, sem chamada à API KAKO).
 */
const BannerRow = ({ label, info, onGoToAgency, testId }) => {
  const [dismissed, setDismissed] = useState(false);
  if (!info || dismissed) return null;

  const expired = info.expired;
  const expiringSoon = info.expiring_soon;
  if (!expired && !expiringSoon) return null;

  const days = info.days_remaining;
  const expiresAt = info.expires_at
    ? new Date(info.expires_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  return (
    <div
      data-testid={testId}
      className={`rounded-lg border-2 px-4 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap ${
        expired
          ? 'bg-red-50 border-red-400'
          : 'bg-amber-50 border-amber-400'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <AlertTriangle className={`w-6 h-6 shrink-0 ${expired ? 'text-red-600 animate-pulse' : 'text-amber-600'}`} />
        <div className="min-w-0">
          <p className={`font-bold text-sm ${expired ? 'text-red-800' : 'text-amber-800'}`}>
            {expired
              ? `🚨 ${label} EXPIRADO!`
              : `⚠️ ${label} expira em ${days === 0 ? 'menos de 1 dia' : `${days} dia${days === 1 ? '' : 's'}`}`}
          </p>
          <p className={`text-xs ${expired ? 'text-red-700' : 'text-amber-700'}`}>
            {expired
              ? 'Funções que dependem deste token estão fora do ar. Substitua o token imediatamente.'
              : `Validade até ${expiresAt}. Substitua o token para evitar indisponibilidade.`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          data-testid={`${testId}-action`}
          size="sm"
          onClick={onGoToAgency}
          className={expired ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
        >
          <KeyRound className="w-4 h-4 mr-1" />
          Substituir token
        </Button>
        {!expired && (
          <Button
            data-testid={`${testId}-dismiss`}
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
            title="Dispensar por agora"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

const TokenExpiryBanner = ({ onGoToAgency }) => {
  const [info, setInfo] = useState(null);
  const [dmInfo, setDmInfo] = useState(null);

  useEffect(() => {
    api.get('/agency/token/status', { params: { quick: true } })
      .then((res) => setInfo(res.data))
      .catch(() => {});
    api.get('/agency/dm-token/status', { params: { quick: true } })
      .then((res) => {
        if (res.data.configured) setDmInfo(res.data);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <BannerRow
        label="Token da agência KAKO"
        info={info}
        onGoToAgency={onGoToAgency}
        testId="token-expiry-banner"
      />
      <BannerRow
        label="Token de DM (Chatbot)"
        info={dmInfo}
        onGoToAgency={onGoToAgency}
        testId="dm-token-expiry-banner"
      />
    </>
  );
};

export default TokenExpiryBanner;
